import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Pencil, Trash2, Building2, X } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Contact } from "@shared/schema";

function avatarInitials(prenom: string, nom: string) {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "?";
}
function avatarColor(name: string) {
  const colors = ["bg-blue-500","bg-emerald-500","bg-violet-500","bg-orange-500","bg-pink-500","bg-teal-500","bg-indigo-500","bg-amber-500"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}
function orgTypeLabel(type: string | null | undefined) {
  const map: Record<string, string> = { client: "Client", revendeur: "Revendeur", prospect: "Prospect", fournisseur: "Fournisseur", partenaire: "Partenaire", autre: "Autre" };
  return type ? (map[type] || type) : null;
}

type ContactWithCount = Contact & { organisationNom?: string; taskCount: number };

export default function IncompleteContactsPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [filterOrg, setFilterOrg] = useState("all");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: allContacts = [], isLoading } = useQuery<ContactWithCount[]>({
    queryKey: ["/api/contacts/incomplete"],
    queryFn: async () => {
      const res = await authFetch("/api/contacts/incomplete");
      if (!res.ok) throw new Error("Erreur chargement");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/contacts/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur suppression");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/contacts/incomplete"] });
      qc.invalidateQueries({ queryKey: ["/api/contacts"] });
      toast({ title: "Contact supprimé" });
      setDeleteId(null);
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const orgOptions = [...new Set(allContacts.map(c => c.organisationNom || c.organisationLibre || c.organisationType).filter(Boolean))] as string[];

  const filtered = allContacts.filter(c => {
    if (filterOrg !== "all") {
      const orgVal = c.organisationNom || c.organisationLibre || c.organisationType || "";
      if (orgVal !== filterOrg) return false;
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      return (
        c.prenom.toLowerCase().includes(s) || c.nom.toLowerCase().includes(s) ||
        (c.email && c.email.toLowerCase().includes(s)) ||
        ((c.organisationNom || c.organisationLibre || "") as string).toLowerCase().includes(s)
      );
    }
    return true;
  }).sort((a, b) => (b.taskCount || 0) - (a.taskCount || 0));

  const deleteTarget = deleteId ? allContacts.find(c => c.id === deleteId) : null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {isLoading ? "Chargement…" : `${filtered.length} fiche${filtered.length !== 1 ? "s" : ""} avec un nom non renseigné`}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 pr-8"
            data-testid="input-search-incomplete"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {orgOptions.length > 0 && (
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger className="w-48" data-testid="select-filter-org">
              <SelectValue placeholder="Organisation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les organisations</SelectItem>
              {orgOptions.map(o => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Chargement…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-52 gap-3 border-2 border-dashed rounded-xl text-muted-foreground">
          <p className="font-medium text-foreground">Aucune fiche à compléter 🎉</p>
          <p className="text-sm">Tous les contacts ont un nom correctement renseigné.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/40 border-b">
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Organisation</th>
                <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Email</th>
                <th className="text-center px-4 py-3 font-medium">Tâches</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Créé le</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((contact, i) => (
                <tr key={contact.id} className={`border-b hover:bg-muted/30 ${i % 2 === 0 ? "" : "bg-muted/10"}`} data-testid={`row-incomplete-${contact.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarFallback className={`text-[10px] font-bold text-white ${avatarColor(contact.prenom + contact.nom)}`}>
                          {avatarInitials(contact.prenom, contact.nom)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-amber-700 dark:text-amber-400">
                          {contact.prenom} {contact.nom}
                        </p>
                        {contact.poste && <p className="text-xs text-muted-foreground">{contact.poste}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">
                    {contact.organisationNom || contact.organisationLibre ? (
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3 shrink-0" />
                        {contact.organisationNom || contact.organisationLibre}
                        {contact.organisationType && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1 ml-1">{orgTypeLabel(contact.organisationType)}</Badge>
                        )}
                      </span>
                    ) : (
                      <span className="italic text-muted-foreground/60">Non rattaché</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground text-xs">
                    {contact.email || <span className="italic text-muted-foreground/60">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={contact.taskCount > 0 ? "secondary" : "outline"} className="text-xs">
                      {contact.taskCount}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                    {contact.createdAt ? format(new Date(contact.createdAt), "d MMM yyyy", { locale: fr }) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => navigate(`/contacts/${contact.id}`)}
                        title="Compléter la fiche"
                        data-testid={`btn-edit-incomplete-${contact.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {contact.taskCount === 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(contact.id)}
                          title="Supprimer"
                          data-testid={`btn-delete-incomplete-${contact.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={v => { if (!v) setDeleteId(null); }}>
        <AlertDialogContent onCloseAutoFocus={e => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contact ?</AlertDialogTitle>
            <AlertDialogDescription>
              La fiche <strong>{deleteTarget?.prenom} {deleteTarget?.nom}</strong> sera supprimée définitivement. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
